"use client";

import * as React from "react";
import { UI_LOCALE_EVENT, UI_LOCALE_STORAGE_KEY } from "./useUiLocale";

type ScopeMode = "user" | "company";

const FALLBACK = [
  { id: "de-CH", label: "DE" },
  { id: "fr-CH", label: "FR" },
  { id: "it-CH", label: "IT" },
  { id: "en-CH", label: "EN" }
];

export default function TopbarLocale(input?: { initialScope?: ScopeMode; showScopeSelect?: boolean; className?: string }) {
  const initialScope = input?.initialScope || "user";
  const showScopeSelect = input?.showScopeSelect !== false;
  const [busy, setBusy] = React.useState(false);
  const [scope, setScope] = React.useState<ScopeMode>(initialScope);
  const [locale, setLocale] = React.useState("de-CH");
  const [items, setItems] = React.useState(FALLBACK);

  React.useEffect(() => {
    setScope(initialScope);
  }, [initialScope]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/api/locale", { cache: "no-store" });
        const json = await res.json();
        if (!alive || !res.ok || !json.ok) return;
        const supported: string[] = Array.isArray(json.supportedLocales) ? json.supportedLocales : FALLBACK.map((x) => x.id);
        const labels: Record<string, string> = json.labels || {};
        setItems(supported.map((id) => ({ id, label: labels[id] || id })));
        const next = String(json.effectiveLocale || "de-CH");
        setLocale(next);
        try {
          localStorage.setItem(UI_LOCALE_STORAGE_KEY, next);
        } catch {
          // Ignore storage failures.
        }
      } catch {
        // Keep fallback.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function save(nextLocale: string, nextScope: ScopeMode) {
    setBusy(true);
    setLocale(nextLocale);
    try {
      const res = await fetch("/api/locale", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locale: nextLocale, scope: nextScope })
      });
      const json = await res.json();
      if (!res.ok || !json.ok) return;
      const next = String(json.effectiveLocale || nextLocale);
      setLocale(next);
      try {
        localStorage.setItem(UI_LOCALE_STORAGE_KEY, next);
      } catch {
        // Ignore storage failures.
      }
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(UI_LOCALE_EVENT, { detail: { locale: next } }));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`topbar-locale ${input?.className || ""}`.trim()}>
      <select
        className="topbar-locale-select"
        value={locale}
        disabled={busy}
        onChange={(e) => save(e.target.value, scope)}
        aria-label="Sprache"
      >
        {items.map((it) => (
          <option key={it.id} value={it.id}>
            {it.label}
          </option>
        ))}
      </select>
      {showScopeSelect ? (
        <select
          className="topbar-locale-scope"
          value={scope}
          disabled={busy}
          onChange={(e) => setScope(e.target.value === "company" ? "company" : "user")}
          aria-label="Sprachbereich"
        >
          <option value="user">User</option>
          <option value="company">Mandat</option>
        </select>
      ) : null}
    </div>
  );
}

